/**
 * Ersatz für den Singleton `DataService.Gruppenkennzeichen` des Altclients: Die Liste wird
 * genau einmal geladen und von allen Auswahllisten geteilt. Der Cache hält das Promise —
 * so teilen sich auch gleichzeitig eingehängte Instanzen denselben Ladevorgang.
 */

import { useEffect, useMemo, useState } from 'react';

import { gruppenkennzeichenApi } from '@/api/services/gruppenkennzeichenApi';
import type { ChinaAppSysGruppenkennzeichen } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';

let cache: Promise<ChinaAppSysGruppenkennzeichen[]> | null = null;

function loadGruppenkennzeichen(): Promise<ChinaAppSysGruppenkennzeichen[]> {
  cache ??= gruppenkennzeichenApi.get();
  return cache;
}

/** Verwirft den geteilten Ladevorgang — für Tests und nach einem Appwechsel. */
export function resetGruppenkennzeichenCache(): void {
  cache = null;
}

/**
 * Liefert die Gruppenkennzeichen eines Feldes — Pendant zu
 * `DataService.Gruppenkennzeichen.Where(e => e.Entitiy == Entity && e.Fieldname == FieldName)`.
 */
export function useGruppenkennzeichen(
  entity: EntityTypes,
  fieldName: string,
): ChinaAppSysGruppenkennzeichen[] {
  const [all, setAll] = useState<ChinaAppSysGruppenkennzeichen[]>([]);

  useEffect(() => {
    let active = true;
    void loadGruppenkennzeichen().then((data) => {
      if (active) setAll(data);
    });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(
    () => all.filter((item) => item.Entitiy === entity && item.Fieldname === fieldName),
    [all, entity, fieldName],
  );
}
